import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-hot-toast';
import {
  Plus, Search, Users, Pencil, History, ChevronLeft, ChevronRight, Upload, Trash2, RefreshCw, SlidersHorizontal, X,
} from 'lucide-react';
import { getEmployees, getDepartments, deleteEmployee } from '../api/employees';
import { getActiveTools, syncAllCharges } from '../api/tools';
import AddEmployeeModal from '../components/forms/AddEmployeeModal';
import EditEmployeeModal from '../components/forms/EditEmployeeModal';
import AllocationHistoryModal from '../components/forms/AllocationHistoryModal';
import BulkUploadModal from '../components/forms/BulkUploadModal';

const fmt = (d) =>
  d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';

const Employees = () => {
  const [employees, setEmployees] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [tools, setTools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const limit = 15;

  // Filters
  const [search, setSearch] = useState('');
  const [deptFilter, setDeptFilter] = useState('');
  const [toolFilter, setToolFilter] = useState('');
  const [paidByFilter, setPaidByFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [costMin, setCostMin] = useState('');
  const [costMax, setCostMax] = useState('');
  const [allocFrom, setAllocFrom] = useState('');
  const [allocTo, setAllocTo] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  const [addOpen, setAddOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [historyTarget, setHistoryTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    getDepartments().then((r) => setDepartments(r.data)).catch(() => {});
    getActiveTools().then((r) => setTools(r.data)).catch(() => {});
  }, []);

  const fetchEmployees = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getEmployees({
        search: search || undefined,
        department: deptFilter || undefined,
        tool_id: toolFilter || undefined,
        paid_by: paidByFilter || undefined,
        status: statusFilter || undefined,
        cost_min: costMin || undefined,
        cost_max: costMax || undefined,
        alloc_from: allocFrom || undefined,
        alloc_to: allocTo || undefined,
        page,
        limit,
      });
      setEmployees(res.data.data);
      setTotal(res.data.total);
    } catch {
      toast.error('Failed to load employees.');
    } finally {
      setLoading(false);
    }
  }, [search, deptFilter, toolFilter, paidByFilter, statusFilter, costMin, costMax, allocFrom, allocTo, page]);

  const allFilters = [search, deptFilter, toolFilter, paidByFilter, statusFilter, costMin, costMax, allocFrom, allocTo];
  useEffect(() => { setPage(1); }, allFilters); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { fetchEmployees(); }, [fetchEmployees]);

  const hasActiveFilters = allFilters.some(Boolean);

  const clearFilters = () => {
    setSearch(''); setDeptFilter(''); setToolFilter(''); setPaidByFilter('');
    setStatusFilter(''); setCostMin(''); setCostMax(''); setAllocFrom(''); setAllocTo('');
  };

  const totalPages = Math.ceil(total / limit);

  const handleSyncCharges = async () => {
    setSyncing(true);
    try {
      const res = await syncAllCharges();
      toast.success(res.data.message);
      fetchEmployees();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Sync failed.');
    } finally {
      setSyncing(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteEmployee(deleteTarget.id);
      toast.success(`"${deleteTarget.name}" removed successfully.`);
      setDeleteTarget(null);
      fetchEmployees();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to delete employee.');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Employees</h1>
          <p className="text-sm text-gray-500 mt-1">Manage employees and their AI tool allocations.</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={handleSyncCharges}
            disabled={syncing}
            className="btn-secondary"
            title="Sync monthly charges from current AI tool prices to all active employee allocations"
          >
            <RefreshCw className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} />
            {syncing ? 'Syncing...' : 'Sync Charges'}
          </button>
          <button onClick={() => setBulkOpen(true)} className="btn-secondary">
            <Upload className="w-4 h-4" />
            Bulk Upload
          </button>
          <button onClick={() => setAddOpen(true)} className="btn-primary">
            <Plus className="w-4 h-4" />
            Add Employee
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="card mb-4">
        {/* Top row: search + toggle */}
        <div className="p-4 flex flex-wrap gap-3 items-center border-b border-gray-100">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              className="input pl-9"
              placeholder="Search by name or email..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <button
            onClick={() => setShowFilters((p) => !p)}
            className={`btn-secondary gap-2 ${showFilters ? 'bg-primary-50 border-primary-300 text-primary-700' : ''}`}
          >
            <SlidersHorizontal className="w-4 h-4" />
            Filters
            {hasActiveFilters && (
              <span className="w-2 h-2 rounded-full bg-primary-500 flex-shrink-0" />
            )}
          </button>
          {hasActiveFilters && (
            <button onClick={clearFilters} className="btn-secondary text-xs px-3 gap-1 text-red-500 border-red-200 hover:bg-red-50">
              <X className="w-3 h-3" /> Clear all
            </button>
          )}
        </div>

        {/* Expanded filter panel */}
        {showFilters && (
          <div className="p-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 bg-gray-50 border-b border-gray-100">
            {/* Department */}
            <div>
              <label className="label">Department</label>
              <select className="input" value={deptFilter} onChange={(e) => setDeptFilter(e.target.value)}>
                <option value="">All Departments</option>
                {departments.map((d) => <option key={d} value={d}>{d}</option>)}
              </select>
            </div>

            {/* AI Tool */}
            <div>
              <label className="label">AI Tool</label>
              <select className="input" value={toolFilter} onChange={(e) => setToolFilter(e.target.value)}>
                <option value="">All Tools</option>
                {tools.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </div>

            {/* Paid By */}
            <div>
              <label className="label">Paid By</label>
              <select className="input" value={paidByFilter} onChange={(e) => setPaidByFilter(e.target.value)}>
                <option value="">All</option>
                <option value="Bacancy">Bacancy</option>
                <option value="Client">Client</option>
              </select>
            </div>

            {/* Status */}
            <div>
              <label className="label">Status</label>
              <select className="input" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                <option value="">All</option>
                <option value="active">Active</option>
                <option value="unallocated">Unallocated</option>
              </select>
            </div>

            {/* Monthly Charge range */}
            <div>
              <label className="label">Min Charge (USD)</label>
              <input
                className="input"
                type="number" min="0" step="0.01" placeholder="0.00"
                value={costMin}
                onChange={(e) => setCostMin(e.target.value)}
              />
            </div>
            <div>
              <label className="label">Max Charge (USD)</label>
              <input
                className="input"
                type="number" min="0" step="0.01" placeholder="Any"
                value={costMax}
                onChange={(e) => setCostMax(e.target.value)}
              />
            </div>

            {/* Allocation Period */}
            <div>
              <label className="label">Alloc. Start From</label>
              <input
                className="input" type="date"
                value={allocFrom}
                onChange={(e) => setAllocFrom(e.target.value)}
              />
            </div>
            <div>
              <label className="label">Alloc. Start To</label>
              <input
                className="input" type="date"
                value={allocTo}
                onChange={(e) => setAllocTo(e.target.value)}
              />
            </div>
          </div>
        )}
      </div>

      {/* Table */}
      <div className="card">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50">
                <th className="text-left px-5 py-3 font-medium text-gray-500">#</th>
                <th className="text-left px-5 py-3 font-medium text-gray-500">Employee</th>
                <th className="text-left px-5 py-3 font-medium text-gray-500">Department</th>
                <th className="text-left px-5 py-3 font-medium text-gray-500">AI Tool</th>
                <th className="text-left px-5 py-3 font-medium text-gray-500">Monthly Charge</th>
                <th className="text-left px-5 py-3 font-medium text-gray-500">Paid By</th>
                <th className="text-left px-5 py-3 font-medium text-gray-500">Allocation Period</th>
                <th className="text-left px-5 py-3 font-medium text-gray-500">Status</th>
                <th className="text-right px-5 py-3 font-medium text-gray-500">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {loading ? (
                <tr>
                  <td colSpan={9} className="px-5 py-12 text-center">
                    <div className="flex flex-col items-center gap-2 text-gray-400">
                      <svg className="animate-spin h-6 w-6 text-primary-500" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                      </svg>
                      Loading...
                    </div>
                  </td>
                </tr>
              ) : employees.length === 0 ? (
                <tr>
                  <td colSpan={9} className="px-5 py-16 text-center">
                    <div className="flex flex-col items-center gap-3 text-gray-400">
                      <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center">
                        <Users className="w-6 h-6" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-500">No employees found</p>
                        <p className="text-xs mt-0.5">
                          {hasActiveFilters ? 'Try adjusting your filters.' : 'Click "Add Employee" to get started.'}
                        </p>
                      </div>
                    </div>
                  </td>
                </tr>
              ) : (
                employees.map((emp, idx) => (
                  <tr key={emp.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-5 py-4 text-gray-400">{(page - 1) * limit + idx + 1}</td>
                    <td className="px-5 py-4">
                      <div>
                        <p className="font-medium text-gray-900">{emp.name}</p>
                        <p className="text-xs text-gray-400">{emp.email}</p>
                      </div>
                    </td>
                    <td className="px-5 py-4">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-50 text-purple-700">
                        {emp.department}
                      </span>
                    </td>
                    <td className="px-5 py-4">
                      {emp.tool_name ? (
                        <div>
                          <p className="font-medium text-gray-800">{emp.tool_name}</p>
                          {emp.tool_identifier && (
                            <p className="text-xs text-gray-400">ID: {emp.tool_identifier}</p>
                          )}
                        </div>
                      ) : (
                        <span className="text-gray-400 text-xs">No tool assigned</span>
                      )}
                    </td>
                    <td className="px-5 py-4 text-sm text-gray-700">
                      {emp.monthly_cost != null
                        ? <span className="font-medium">${Number(emp.monthly_cost).toFixed(2)}</span>
                        : <span className="text-gray-400 text-xs">—</span>}
                    </td>
                    <td className="px-5 py-4">
                      {emp.paid_by ? (
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          emp.paid_by === 'Client'
                            ? 'bg-blue-50 text-blue-700'
                            : 'bg-green-50 text-green-700'
                        }`}>
                          {emp.paid_by}
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-50 text-green-700">
                          Bacancy
                        </span>
                      )}
                    </td>
                    <td className="px-5 py-4 text-xs text-gray-500">
                      {emp.start_date ? (
                        <span>
                          {fmt(emp.start_date)} → {emp.end_date ? fmt(emp.end_date) : <span className="text-gray-400 italic">No end date</span>}
                        </span>
                      ) : '—'}
                    </td>
                    <td className="px-5 py-4">
                      {emp.allocation_status === 'active' ? (
                        <span className="badge-active">Active</span>
                      ) : emp.allocation_status === 'migrated' ? (
                        <span className="badge-migrated">Migrated</span>
                      ) : emp.allocation_status ? (
                        <span className="badge-expired">Expired</span>
                      ) : (
                        <span className="badge-expired">Unallocated</span>
                      )}
                    </td>
                    <td className="px-5 py-4">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => setEditTarget(emp)}
                          className="p-1.5 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                          title="Edit Employee"
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => setHistoryTarget(emp)}
                          className="p-1.5 text-gray-400 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                          title="View History"
                        >
                          <History className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => setDeleteTarget(emp)}
                          className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Delete Employee"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {!loading && total > limit && (
          <div className="flex items-center justify-between px-5 py-3 border-t border-gray-100 text-sm">
            <p className="text-gray-500">
              Showing {(page - 1) * limit + 1}–{Math.min(page * limit, total)} of {total}
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="p-1.5 rounded-lg border border-gray-200 disabled:opacity-40 hover:bg-gray-50"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="text-gray-600 px-1">{page} / {totalPages}</span>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="p-1.5 rounded-lg border border-gray-200 disabled:opacity-40 hover:bg-gray-50"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        {!loading && employees.length > 0 && total <= limit && (
          <div className="px-5 py-3 border-t border-gray-100 text-xs text-gray-400">
            {total} employee{total !== 1 ? 's' : ''} total
          </div>
        )}
      </div>

      {/* Modals */}
      <BulkUploadModal
        isOpen={bulkOpen}
        onClose={() => setBulkOpen(false)}
        onSaved={fetchEmployees}
      />
      <AddEmployeeModal
        isOpen={addOpen}
        onClose={() => setAddOpen(false)}
        onSaved={fetchEmployees}
        departments={departments}
      />
      <EditEmployeeModal
        isOpen={!!editTarget}
        onClose={() => setEditTarget(null)}
        onSaved={fetchEmployees}
        employee={editTarget}
        departments={departments}
      />
      <AllocationHistoryModal
        isOpen={!!historyTarget}
        onClose={() => setHistoryTarget(null)}
        employee={historyTarget}
      />

      {/* Delete Confirmation */}
      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => !deleting && setDeleteTarget(null)} />
          <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-sm z-10 p-6">
            <div className="flex items-center justify-center w-12 h-12 bg-red-100 rounded-full mx-auto mb-4">
              <Trash2 className="w-6 h-6 text-red-600" />
            </div>
            <h3 className="text-base font-semibold text-gray-900 text-center">Delete Employee?</h3>
            <p className="text-sm text-gray-500 text-center mt-2">
              This will permanently remove <span className="font-semibold text-gray-800">{deleteTarget.name}</span> and all their allocation history. This cannot be undone.
            </p>
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setDeleteTarget(null)}
                disabled={deleting}
                className="btn-secondary flex-1"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="btn-danger flex-1"
              >
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Employees;
